import React, { useCallback, useEffect, useMemo, useState } from "react";
import TableContainer from "../../components/Common/TableContainer";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { Button, Card, CardBody, Container } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import useFetchData from "../../custom/Utils/UseFetchDataUtils";
import usePagination from "../../custom/Utils/UsePaginationUtils";
import useListFilters from "../../custom/Utils/UseListFiltersUtils";
import { listPageData } from "./OutgoingInvoicesFile";
import SmartImportButton from "../../custom/components/Import/SmartImportButton.js";
import { getRecords } from "../../services/api.service";
import {
  areAllValuesIdentical,
  smartSort,
} from "../../custom/Utils/ColumnUtils";

const breadcrumbItems = [];

const OutgoingInvoicesList = () => {
  const navigate = useNavigate();
  const [exportData, setExportData] = useState([]);

  const {
    payload,
    endpoint,
    searchfields,
    exportFileName,
    navigatePage,
    filterConfig,
  } = listPageData();
  const initialPayload = useMemo(() => payload, []);

  // pagination
  const {
    page,
    pageSize,
    nextPage,
    previousPage,
    lastPage,
    firstPage,
    handlePageSizeChange,
    setPage,
  } = usePagination();

  // list filters (config comes from OutgoingInvoicesFile.js)
  const {
    filters,
    filterValues,
    handleFilterChange,
    resetFilters,
    filterWhereCondition,
    appliedFilterCount,
  } = useListFilters(filterConfig);

  // fetch data using the provided payload, page, and page size
  const { data, isLoading, setSearchKeyword, totalCount, refetch } = useFetchData(
    initialPayload,
    page,
    pageSize,
    endpoint,
    searchfields,
    { filterWhere: filterWhereCondition, onResetPage: firstPage }
  );

  const handleViewClick = (id) => {
    navigate(`/outgoing-invoice-view/${id}`);
  };

  // dispatch_ids is a plain JSONB array on outgoing_invoices - there is no
  // real association back to dispatcheds/material_intakes, so the serial
  // number(s) for the current page are resolved with a small side fetch and
  // merged into the rows client-side.
  const [serialNoByDispatchId, setSerialNoByDispatchId] = useState({});

  useEffect(() => {
    const dispatchIds = Array.from(
      new Set((data || []).flatMap((row) => row?.dispatch_ids || []))
    );

    if (!dispatchIds.length) {
      setSerialNoByDispatchId({});
      return;
    }

    const fetchSerialNumbers = async () => {
      try {
        const payload = {
          modelName: "dispatcheds",
          schema: localStorage.getItem("schema"),
          whereCondition: { id: dispatchIds },
          include: [{ model: "material_intakes", as: "intake" }],
        };
        const response = await getRecords(payload, "/getRecords");
        const map = {};
        (response?.data || []).forEach((dispatch) => {
          map[dispatch.id] = dispatch?.intake?.material_serial_no || "-";
        });
        setSerialNoByDispatchId(map);
      } catch (error) {
        console.error("Error fetching dispatch serial numbers:", error);
      }
    };

    fetchSerialNumbers();
  }, [data]);

  const rowsWithSerialNo = useMemo(
    () =>
      (data || []).map((row) => ({
        ...row,
        serial_no: (row?.dispatch_ids || [])
          .map((id) => serialNoByDispatchId[id])
          .filter(Boolean)
          .join(", "),
      })),
    [data, serialNoByDispatchId]
  );

  // handle page size change callback
  const handlePageSizeChangeWrapper = useCallback(
    (newSize) => {
      handlePageSizeChange(newSize, totalCount);
    },
    [totalCount],
  );
  const handleCreate = () => navigate(navigatePage.create);

  const columns = useMemo(
    () => [
      {
        Header: "SR.NO",
        accessor: "index",
        accessor: (row) => page * pageSize + row.index,
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "50px" }}>{value}</div>,
      },
      {
        Header: "SERIAL NO",
        accessor: (row) => (row?.serial_no ? row?.serial_no : "-"),
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "OUTGOING INVOICE NO",
        accessor: (row) => (row?.invoice_number ? row?.invoice_number : "-"),
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "ZONE",
        accessor: (row) => (row?.zone?.zone_name ? row?.zone?.zone_name : "-"),

        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "BRANCH",
        accessor: (row) =>
          row?.branch?.branch_name ? row?.branch?.branch_name : "-",
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "Status",
        accessor: (row) =>
          row?.status == 1 ? "Courier Pending" : "Courier Completed",

        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },

      {
        Header: "IS ACTIVE",
        accessor: "is_active",
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        disableSortBy: areAllValuesIdentical(data, "is_active"),
        Cell: ({ value }) => (
          <div style={{ width: "100px" }}>
            {value === false || value === 0 ? (
              <span className="badge bg-danger-subtle text-danger text-uppercase">
                In-active
              </span>
            ) : (
              <span className="badge bg-success-subtle text-success text-uppercase">
                Active
              </span>
            )}
          </div>
        ),
      },
      {
        Header: "ACTION",
        accessor: "actions",
        disableFilters: true,
        filterable: false,
        Cell: ({ row }) => (
          <div style={{ width: "150px", display: "flex" }}>
            <div
              onClick={() => {
                handleViewClick(row.original.id);
              }}
            >
              <Link className="me-3 text-primary">
                <i className="mdi mdi-eye font-size-18"></i>
              </Link>
            </div>
          </div>
        ),
      },
    ],
    [page, pageSize, data],
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <Breadcrumb
              title="Outgoing Invoices List"
              breadcrumbItems={breadcrumbItems}
            />
            <Button
              color="primary"
              className="waves-effect waves-light me-1"
              onClick={handleCreate}
              style={{ marginLeft: "auto", background: "#006eb9" }}
            >
              Create Outgoing Invoice
            </Button>
            <SmartImportButton
              module="outgoing-invoice"
              label="Outgoing Invoice"
              onImported={refetch}
            />
          </div>

          <Card>
            <CardBody>
              <TableContainer
                columns={columns || []}
                data={rowsWithSerialNo || []}
                isLoading={isLoading}
                onSearchInputData={setSearchKeyword}
                pageIndexProp={page}
                onPageSizeChange={handlePageSizeChangeWrapper}
                nextToPage={nextPage}
                lastToPage={() => lastPage(totalCount)}
                previousToPage={previousPage}
                previousFirstToPage={firstPage}
                totalCount={totalCount}
                customPageSize={pageSize}
                filters={filters}
                filterValues={filterValues}
                onFilterChange={handleFilterChange}
                onFilterReset={resetFilters}
                appliedFilterCount={appliedFilterCount}
                exportData={exportData}
                exportFileName={exportFileName}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default OutgoingInvoicesList;
